//---------------------------------------------------------------------------------------------------
// ## IMPORTS ##
// Library Imports
import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import { MediaType } from "@virtus-tech-repository/virtus-tech-repository/lib/components/VTTimelineImageItem";
import { EMedia, IMediaItem } from "@virtus-tech-repository/virtus-tech-repository/lib/models/media.model";

// Custom components imports (and hooks and helpers)

export const MediaApi = createApi({
    reducerPath: "media",
    tagTypes: [
        "media_image",
        "media_three_sixty_video",
        "media_video",
        "media_three_sixty_image",
        "media_audio",
        "media",
        "preSignUrl",
    ],
    baseQuery: fetchBaseQuery({
        baseUrl: process.env.REACT_APP_NINJA_MEDIA_CONFIG,
    }),
    endpoints: (builder) => ({
        getAllImages: builder.query<IMediaItem[], string>({
            query: (userId) => ({
                url: `images/${userId}`,
                method: "GET",
            }),

            providesTags: (result) =>
                result
                    ? [
                          ...result.map(({ id }: IMediaItem) => ({
                              type: "media_image" as const,
                              id,
                          })),
                          { type: "media_image", id: "LIST" },
                      ]
                    : [{ type: "media_image", id: "LIST" }],
        }),
        // TODO: type just getting it working sorry chris ;) you naughty boi
        updateAllImages: builder.mutation<any, Partial<any>>({
            query: (body) => ({
                url: "mediametadata",
                method: "POST",
                body,
            }),
            invalidatesTags: [{ type: "media_image", id: "LIST" }],
        }),
        deleteMedia: builder.mutation<any, { mediaType: EMedia; collectionId: string; mediaId: string }>({
            query: (body) => ({
                url: `${body.mediaType}/${body.collectionId}/${body.mediaId}`,
                method: "DELETE",
            }),
            invalidatesTags: [
                { type: "media_three_sixty_image", id: "LIST" },
                { type: "media_three_sixty_video", id: "LIST" },
                { type: "media_image", id: "LIST" },
                { type: "media_video", id: "LIST" },
            ],
        }),
        getAllVideos: builder.query<IMediaItem[], void>({
            query: () => ({
                url: process.env.REACT_APP_IS_DEV ? "videos.json" : "videos",
                method: "GET",
            }),

            providesTags: (result) =>
                result
                    ? [
                          ...result.map(({ id }: IMediaItem) => ({
                              type: "media" as const,
                              id,
                          })),
                          { type: "media_video", id: "LIST" },
                      ]
                    : [{ type: "media_video", id: "LIST" }],
        }),
        // TODO: type just getting it working sorry chris ;)
        updateAllVideos: builder.mutation<any, Partial<any>>({
            query: (body) => ({
                url: "mediametadata",
                method: "POST",
                body,
            }),
            invalidatesTags: [{ type: "media_video", id: "LIST" }],
        }),
        getAllAudio: builder.query<IMediaItem[], string>({
            query: (userId) => ({
                url: `audio/${userId}`,
                method: "GET",
            }),

            providesTags: (result) =>
                result
                    ? [
                          ...result.map(({ id }: IMediaItem) => ({
                              type: "media" as const,
                              id,
                          })),
                          { type: "media_audio", id: "LIST" },
                      ]
                    : [{ type: "media_audio", id: "LIST" }],
        }),
        // TODO: type just getting it working sorry chris ;)
        updateAllAudio: builder.mutation<any, Partial<any>>({
            query: (body) => ({
                url: "mediametadata",
                method: "POST",
                body,
            }),
            invalidatesTags: [{ type: "media_audio", id: "LIST" }],
        }),
        getAll360Images: builder.query<IMediaItem[], string>({
            query: (userId) => ({
                url: `three_sixty_image/${userId}`,
                method: "GET",
            }),

            providesTags: (result) =>
                result
                    ? [
                          ...result.map(({ id }: IMediaItem) => ({
                              type: "media" as const,
                              id,
                          })),
                          { type: "media_three_sixty_image", id: "LIST" },
                      ]
                    : [{ type: "media_three_sixty_image", id: "LIST" }],
        }),

        getAll360Videos: builder.query<IMediaItem[], string>({
            query: (userId) => ({
                url: `three_sixty_video/${userId}`,
                method: "GET",
            }),

            providesTags: (result) =>
                result
                    ? [
                          ...result.map(({ id }: IMediaItem) => ({
                              type: "media" as const,
                              id,
                          })),
                          { type: "media_three_sixty_video", id: "LIST" },
                      ]
                    : [{ type: "media_three_sixty_video", id: "LIST" }],
        }),
        // TODO: type just getting it working sorry chris ;)
        updateAllThreeSixtyVideos: builder.mutation<any, Partial<any>>({
            query: (body) => ({
                url: "mediametadata",
                method: "POST",
                body,
            }),
            invalidatesTags: [{ type: "media_three_sixty_video", id: "LIST" }],
        }),
        // TODO: type just getting it working sorry chris ;)
        updateAllThreeSixtyImages: builder.mutation<any, Partial<any>>({
            query: (body) => ({
                url: "mediametadata",
                method: "POST",
                body,
            }),
            invalidatesTags: [{ type: "media_three_sixty_image", id: "LIST" }],
        }),
        getAllVideos2d: builder.query<IMediaItem[], string>({
            query: (userId) => ({
                url: `video/${userId}`,
                method: "GET",
            }),

            providesTags: (result) =>
                result
                    ? [
                          ...result.map(({ id }: IMediaItem) => ({
                              type: "media" as const,
                              id,
                          })),
                          { type: "media_video", id: "LIST" },
                      ]
                    : [{ type: "media_video", id: "LIST" }],
        }),
        preSignedUrl: builder.mutation<
            { url: string },
            Partial<{
                organisation: string;
                media_type: string;
                media_id: string;
                filetype: string;
            }>
        >({
            query: (body) => ({
                url: "presignedurl",
                method: "POST",
                body,
            }),
            invalidatesTags: [{ type: "preSignUrl", id: "LIST" }],
        }),
    }),
});

export const {
    useGetAllImagesQuery,
    useGetAll360ImagesQuery,
    useGetAll360VideosQuery,
    useGetAllAudioQuery,
    useGetAllVideos2dQuery,
    usePreSignedUrlMutation,
    useDeleteMediaMutation,
    useUpdateAllImagesMutation,
    useUpdateAllThreeSixtyVideosMutation,
    useUpdateAllVideosMutation,
    useUpdateAllAudioMutation,
    useUpdateAllThreeSixtyImagesMutation,
} = MediaApi;
